<template>
  <div class="login">
    <!-- Page Header -->
    <section class="page-header">
      <div class="container">
        <h1>Login</h1>
        <p class="page-subtitle">Access your SC PBC account</p>
      </div>
    </section>

    <!-- Login Form -->
    <section class="section">
      <div class="container">
        <div class="form-card">
          <form @submit.prevent="onSubmit($event)" class="login-form">
            <div class="form-group">
              <label for="cell">Phone Number</label>
              <input
                id="cell"
                v-model="formData.cell"
                type="tel"
                placeholder="Enter your phone number"
                required
                :disabled="otpRequested"
                class="form-input"
              />
            </div>

            <div class="form-group" v-if="otpRequested">
              <label for="otp">One Time Password (OTP)</label>
              <input
                id="otp"
                v-model="formData.otp"
                type="text"
                placeholder="Enter the OTP sent to your phone"
                required
                maxlength="6"
                pattern="[0-9]{6}"
                class="form-input"
              />
              <small class="form-hint">Please check your phone for the OTP code</small>
            </div>

            <div v-if="errorMessage" class="alert alert-error">
              {{ errorMessage }}
            </div>

            <div v-if="successMessage" class="alert alert-success">
              {{ successMessage }}
            </div>

            <button type="submit" class="btn btn-primary btn-block" :disabled="loading">
              <span v-if="loading">Processing...</span>
              <span v-else>{{ otpRequested ? 'Verify OTP' : 'Get One Time Password' }}</span>
            </button>

            <button
              v-if="!otpRequested"
              type="submit"
              name="register"
              class="btn btn-outline btn-block"
              :disabled="loading"
            >
              Register as New Borrower
            </button>

            <div v-if="!otpRequested && otpMessage" class="otp-message">
              {{ otpMessage }}
            </div>

            <button
              v-if="otpRequested"
              type="button"
              @click="resetForm"
              class="btn btn-outline btn-block"
            >
              Request New OTP
            </button>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'Login',
  props: {
    otpMessage: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      successMessage: '',
      otpRequested: false,
      formData: {
        cell: '',
        otp: ''
      }
    };
  },
  methods: {
    onSubmit(event) {
      const submitter = event && event.submitter;
      if (submitter && submitter.name === 'register') {
        const query = this.formData.cell ? { cell: this.formData.cell } : {};
        this.$router.push({ name: 'Register', query });
        return;
      }

      if (this.otpRequested) {
        this.handleOTPSubmit();
      } else {
        this.handleRequestOTP();
      }
    },
    async handleRequestOTP() {
      this.loading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const response = await axios.post('/api/auth/request-otp', {
          cell: this.formData.cell
        });

        if (response.data.success) {
          this.successMessage = 'OTP has been sent to your phone number!';
          this.otpRequested = true;
        }
      } catch (error) {
        if (error.response?.status === 404) {
          this.$router.push({
            name: 'Register',
            query: { cell: this.formData.cell }
          });
        } else {
          this.errorMessage = error.response?.data?.message ||
            'Failed to send OTP. Please try again.';
        }
      } finally {
        this.loading = false;
      }
    },
    async handleOTPSubmit() {
      this.loading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const response = await axios.post('/api/auth/verify-otp', {
          cell: this.formData.cell,
          otp: this.formData.otp
        });

        if (response.data.success) {
          this.successMessage = 'OTP verified successfully!';

          if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }

          setTimeout(() => {
            this.$router.push('/sc-member-dashboard');
          }, 1000);
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.message ||
          'Invalid OTP. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.otpRequested = false;
      this.formData.otp = '';
      this.errorMessage = '';
      this.successMessage = '';
    }
  }
};
</script>

<style scoped>
/* Page Header */
.page-header {
  background: hsl(155, 100%, 22%);
  color: #fff;
  padding: 60px 24px;
  text-align: center;
}

.page-header h1 {
  margin-bottom: 8px;
}

.page-subtitle {
  font-size: 18px;
  opacity: 0.9;
  margin: 0;
}

/* Form Card */
.form-card {
  max-width: 440px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 40px;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: hsl(0, 0%, 20%);
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 14px 16px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: hsl(155, 100%, 22%);
  box-shadow: 0 0 0 3px hsla(155, 100%, 22%, 0.1);
}

.form-input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.form-hint {
  display: block;
  margin-top: 8px;
  font-size: 13px;
  color: #666;
}

/* Alerts */
.alert {
  padding: 14px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert-error {
  background: #fef2f2;
  color: #dc2626;
  border-left: 4px solid #dc2626;
}

.alert-success {
  background: #f0fdf4;
  color: #16a34a;
  border-left: 4px solid #16a34a;
}

/* Buttons */
.btn-block {
  width: 100%;
  min-width: 75px;
  min-height: 75px;
  margin-top: 12px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.otp-message {
  margin-top: 16px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 8px;
  font-size: 14px;
  color: #444;
}

/* Responsive */
@media (max-width: 768px) {
  .form-card {
    padding: 24px;
    margin: 0 16px;
  }
}
</style>
